import { supabase } from './supabaseClient';
import serviceErrorHandler from './errorHandler';
import twilioService from './twilioService';
import registrationService from './registrationService';

class OTPService {
  constructor() {
    // Validate that supabase client is available
    if (!supabase) {
      console.warn('Supabase client not available during OTPService initialization');
    }
  }

  // Enhanced Edge Function error handler with better FunctionsHttpError detection
  _handleEdgeFunctionError(error, functionName) {
    console.error(`Edge Function ${functionName} error:`, error);
    const errorMessage = error?.message?.toLowerCase() || '';

    if (errorMessage?.includes('paused')) {
      return {
        success: false,
        error: '⚠️ Your Supabase project is paused. Resume it in the Supabase dashboard to send OTPs.',
        code: 'SUPABASE_PAUSED',
        requiresConfiguration: false,
        showConfigGuide: false,
        persistent: true,
        retryable: true,
        technicalDetails: error?.message
      };
    }
    
    // Enhanced error logging for both FunctionsFetchError and FunctionsHttpError
    if (error?.name === 'FunctionsFetchError' || error?.name === 'FunctionsHttpError') {
      console.error(`${error?.name} details:`, {
        message: error?.message,
        context: error?.context,
        status: error?.status,
        statusText: error?.statusText,
        stack: error?.stack,
        functionName
      });
    }
    
    // Use centralized error handler for consistent error processing
    const errorResult = serviceErrorHandler?.parseServiceError(error, functionName);
    
    // Log the error for monitoring
    serviceErrorHandler?.logError(errorResult);
    
    // Enhanced handling for FunctionsHttpError (the specific error in the user's case)
    if (error?.name === 'FunctionsHttpError'|| error?.message?.includes('Edge Function returned a non-2xx status code') ||
        error?.message?.includes('returned a non-2xx status')) {
      console.error('FunctionsHttpError detected - edge function returned error status');
      
      return {
        success: false,
        error: '⚠️ SMS service configuration error. Using direct Twilio integration instead.',
        code: 'EDGE_FUNCTION_HTTP_ERROR',
        requiresConfiguration: false, // Direct integration doesn't require Edge Function config
        showConfigGuide: false,
        persistent: false,
        fallbackAvailable: true,
        technicalDetails: 'Edge Function failed, but direct Twilio integration is available.',
        configurationHelp: twilioService?.getConfigurationHelp()
      };
    }
    
    // Handle specific FunctionsFetchError cases (network/deployment issues)
    if (error?.name === 'FunctionsFetchError' || error?.message?.includes('Failed to send a request to the Edge Function')) {
      console.error('FunctionsFetchError detected - using direct Twilio integration');
      
      return {
        success: false,
        error: '⚠️ Edge Function not available. Using direct Twilio integration.',
        code: 'EDGE_FUNCTION_ERROR',
        requiresConfiguration: false,
        showConfigGuide: false,
        persistent: false,
        fallbackAvailable: true,
        technicalDetails: 'Edge Function unavailable, falling back to direct Twilio integration.'
      };
    }
    
    // Return the processed error result
    return errorResult;
  }

  // Store OTP in database for verification with method tracking
  async _storeOTPInDatabase(phoneNumber, otpCode, expiresAt, method = 'whatsapp') {
    try {
      // Get current session for user ID (optional for guest users)
      const user = await registrationService?.getCurrentUser();
      const userId = user?.id || null;

      // Generate session ID for guest users
      const sessionId = userId ? null : `guest_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;

      const { data, error } = await supabase?.from('otp_verifications')?.insert({
        user_id: userId,
        phone_number: phoneNumber,
        otp_code: otpCode,
        expires_at: expiresAt,
        status: 'pending',
        session_id: sessionId,
        verification_method: method, // Track the actual delivery method
        created_at: new Date()?.toISOString()
      })?.select()?.single();

      if (error) {
        console.error('Failed to store OTP in database:', error);
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error('Database storage error:', error);
      throw error;
    }
  }

  // Enhanced OTP generation with direct Twilio integration
  async sendOTP(phoneNumber) {
    try {
      console.log('OTPService: Starting sendOTP for phone:', phoneNumber?.slice(-4)); // Log last 4 digits only
      
      // Validate phone number format
      const phoneRegex = /^[+]?[\d\s\-()]{10,15}$/;
      if (!phoneRegex?.test(phoneNumber)) {
        console.log('Invalid phone number format');
        return { 
          success: false, 
          error: 'Please enter a valid phone number',
          code: 'INVALID_PHONE',
          category: 'validation'
        };
      }

      // Format phone number for consistency
      let formattedPhone = phoneNumber?.replace(/\D/g, '');
      if (formattedPhone?.length === 10) {
        formattedPhone = '+91' + formattedPhone; // Add India country code
      } else if (!formattedPhone?.startsWith('+')) {
        formattedPhone = '+' + formattedPhone;
      }

      console.log('Formatted phone number for processing');

      // Generate OTP code first (used for template-based WhatsApp or fallback storage)
      const otpCode = Math.floor(100000 + Math.random() * 900000)?.toString();
      const expiresAt = new Date(Date.now() + 10 * 60 * 1000)?.toISOString(); // 10 minutes
      
      // Try Twilio Verify service first (preferred)
      if (twilioService?.isVerifyServiceConfigured()) {
        try {
          console.log('Using Twilio Verify WhatsApp integration...');
          const verifyResult = await twilioService?.sendWhatsAppOTP(formattedPhone);

          if (verifyResult?.success) {
            return {
              success: true,
              data: {
                expiresAt,
                message: 'OTP sent successfully to your WhatsApp number',
                instructions: 'Please check your WhatsApp for the verification code.',
                method: 'whatsapp_verify',
                verificationSid: verifyResult?.sid
              }
            };
          }
        } catch (twilioError) {
          console.error('Twilio Verify integration failed:', twilioError);
        }
      }

      // Try direct Twilio template integration
      if (twilioService?.isServiceConfigured()) {
        try {
          console.log('Using direct Twilio WhatsApp integration...');
          
          // Send WhatsApp message via direct Twilio integration
          const twilioResult = await twilioService?.sendWhatsAppOTP(formattedPhone, otpCode);
          
          if (twilioResult?.success) {
            // Store OTP in database for verification with correct method
            try {
              const { data: otpRecord } = await this._storeOTPInDatabase(formattedPhone, otpCode, expiresAt, 'whatsapp');
              
              console.log('OTP sent successfully via direct Twilio integration');
              return { 
                success: true, 
                data: {
                  expiresAt: expiresAt,
                  message: 'OTP sent successfully to your WhatsApp number',
                  instructions: 'Please check your WhatsApp for the verification code.',
                  method: 'whatsapp',
                  messageSid: twilioResult?.messageSid,
                  otpId: otpRecord?.id
                }
              };
            } catch (dbError) {
              console.error('Failed to store OTP in database:', dbError);
              // Continue anyway - OTP was sent successfully
              return { 
                success: true, 
                data: {
                  expiresAt: expiresAt,
                  message: 'OTP sent successfully to your WhatsApp number',
                  instructions: 'Please check your WhatsApp for the verification code.',
                  method: 'whatsapp',
                  messageSid: twilioResult?.messageSid,
                  warning: 'OTP sent but not stored in database. Manual verification may be required.'
                }
              };
            }
          }
        } catch (twilioError) {
          console.error('Direct Twilio integration failed:', twilioError);
          
          // Check for specific WhatsApp sandbox errors
          if (twilioError?.message?.includes('sandbox') || 
              twilioError?.message?.includes('not verified') ||
              twilioError?.message?.includes('join')) {
            return {
              success: false,
              error: `⚠️ WhatsApp Setup Required: ${twilioError?.message}`,
              code: 'WHATSAPP_SANDBOX_ERROR',
              requiresConfiguration: true,
              showConfigGuide: true,
              persistent: true,
              configurationHelp: {
                ...twilioService?.getConfigurationHelp(),
                urgentAction: {
                  title: 'WhatsApp Sandbox Setup Required',
                  message: 'Your phone number needs to be verified in Twilio WhatsApp Sandbox',
                  steps: [
                    '1. Open WhatsApp on your phone',
                    '2. Send a message to +1 415 523 8886 (Twilio Sandbox)',
                    '3. Send this exact message: "join <sandbox-keyword>"',
                    '4. Wait for confirmation message from Twilio',
                    '5. Try sending OTP again',
                    '6. Alternative: Use SMS instead of WhatsApp for testing'
                  ],
                  note: 'This is required only for the Twilio WhatsApp Sandbox. Production WhatsApp API works differently.'
                }
              }
            };
          }
          
          // Check if it's a configuration issue
          if (twilioError?.message?.includes('not configured') || 
              twilioError?.message?.includes('credentials')) {
            return {
              success: false,
              error: `⚠️ WhatsApp OTP configuration needed: ${twilioError?.message}`,
              code: 'TWILIO_CONFIG_ERROR',
              requiresConfiguration: true,
              showConfigGuide: true,
              persistent: true,
              configurationHelp: twilioService?.getConfigurationHelp(),
            };
          }
          
          // Fall through to Edge Function as backup
          console.log('Falling back to Edge Function...');
        }
      } else {
        console.log('Direct Twilio not configured, checking Edge Function...');
      }

      // Fallback to Edge Function approach (original code)
      if (!supabase?.functions) {
        console.error('Neither Twilio nor Supabase functions available');
        return {
          success: false,
          error: 'OTP service not available. Please configure Twilio WhatsApp integration.',
          code: 'SERVICE_NOT_AVAILABLE',
          requiresConfiguration: true,
          showConfigGuide: true,
          configurationHelp: twilioService?.getConfigurationHelp()
        };
      }

      // Get current session for authorization
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError) {
        console.warn('Session error (continuing as guest):', sessionError);
      }
      
      const authToken = session?.access_token;
      console.log('Auth token available:', !!authToken);

      // Enhanced Edge Function call with improved error handling
      const functionCall = async () => {
        console.log('Calling send-otp Edge Function...');
        const result = await supabase?.functions?.invoke('send-otp', {
          body: { phoneNumber: formattedPhone },
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
        });
        console.log('Edge Function response received');
        return result;
      };

      const { data, error } = await this._retryEdgeFunctionCall(functionCall);

      if (error) {
        console.error('Edge function error details:', {
          error,
          message: error?.message,
          name: error?.name,
          status: error?.status,
          stack: error?.stack,
          functionName: 'send-otp'
        });
        
        const errorResult = this._handleEdgeFunctionError(error, 'send-otp');
        
        // If Edge Function fails but we have Twilio config, suggest direct integration
        if (!twilioService?.isServiceConfigured() && errorResult?.code !== 'SUPABASE_PAUSED') {
          errorResult.configurationHelp = twilioService?.getConfigurationHelp();
          errorResult.showConfigGuide = true;
        }
        
        return errorResult;
      }

      if (!data) {
        console.error('No data received from Edge Function');
        return {
          success: false,
          error: 'No response received from SMS service. Consider using direct Twilio integration.',
          code: 'NO_RESPONSE',
          configurationHelp: twilioService?.getConfigurationHelp(),
          showConfigGuide: true
        };
      }

      console.log('Edge Function response data:', { success: data?.success, hasError: !!data?.error, code: data?.code });

      if (!data?.success) {
        console.error('Edge Function returned failure:', data?.error);
        
        return {
          success: false,
          error: data?.error || 'Edge function returned failure',
          code: data?.code || 'EDGE_FUNCTION_FAILED',
          configurationHelp: twilioService?.getConfigurationHelp(),
          showConfigGuide: true,
          requiresConfiguration: true
        };
      }

      console.log('OTP sent successfully via Edge Function');
      return { 
        success: true, 
        data: {
          ...data?.data,
          message: 'OTP sent successfully to your WhatsApp number',
          instructions: 'Please check your WhatsApp for the verification code.',
          method: 'whatsapp'
        }
      };
    } catch (error) {
      console.error('OTP sending error:', error);
      const errorResult = this._handleEdgeFunctionError(error, 'send-otp');
      
      // Enhance with Twilio configuration help
      if (!twilioService?.isServiceConfigured() && errorResult?.code !== 'SUPABASE_PAUSED') {
        errorResult.configurationHelp = twilioService?.getConfigurationHelp();
        errorResult.showConfigGuide = true;
      }
      
      return errorResult;
    }
  }

  // Enhanced retry mechanism with better error categorization for HTTP errors
  async _retryEdgeFunctionCall(functionCall, maxRetries = 2, delay = 1000) {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Edge Function attempt ${attempt + 1}/${maxRetries + 1}`);
        const result = await functionCall();
        console.log('Edge Function call successful');
        return result;
      } catch (error) {
        lastError = error;
        console.error(`Edge Function attempt ${attempt + 1} failed:`, error);
        
        // Don't retry on specific error types that indicate configuration issues
        if (error?.name === 'FunctionsFetchError' || 
            error?.name === 'FunctionsHttpError'|| error?.message?.includes('Failed to send a request to the Edge Function') ||
            error?.message?.includes('Edge Function returned a non-2xx status code') ||
            error?.message?.includes('400') || 
            error?.message?.includes('401') || 
            error?.message?.includes('403') ||
            error?.message?.includes('422') ||
            error?.message?.includes('500') ||
            error?.message?.includes('non-2xx status code')) {
          console.log('Not retrying due to configuration/deployment error');
          break;
        }
        
        // Wait before retrying (exponential backoff)
        if (attempt < maxRetries) {
          const waitTime = delay * Math.pow(2, attempt);
          console.log(`Waiting ${waitTime}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }
    
    throw lastError;
  }

  // Enhanced OTP verification that works for both authenticated and guest users
  async verifyOTP(phoneNumber, otpCode) {
    try {
      if (!supabase?.from) {
        return { 
          success: false, 
          error: 'Verification service not available. Please contact support.',
          code: 'SERVICE_NOT_AVAILABLE'
        };
      }

      if (!otpCode || otpCode?.length !== 6) {
        return { 
          success: false, 
          error: 'Please enter a valid 6-digit OTP code',
          code: 'INVALID_OTP_FORMAT'
        };
      }

      // Format phone number consistently
      let formattedPhone = phoneNumber?.replace(/\D/g, '');
      if (formattedPhone?.length === 10) {
        formattedPhone = '+91' + formattedPhone;
      } else if (!formattedPhone?.startsWith('+')) {
        formattedPhone = '+' + formattedPhone;
      }

      // Try Twilio Verify check first (if configured)
      if (twilioService?.isVerifyServiceConfigured()) {
        try {
          const verifyResult = await twilioService?.checkWhatsAppOTP(formattedPhone, otpCode?.trim());
          if (verifyResult?.success) {
            return {
              success: true,
              data: {
                verifiedAt: new Date()?.toISOString(),
                message: 'Phone number verified successfully',
                verificationMethod: 'twilio_verify'
              }
            };
          }

          return {
            success: false,
            error: 'Invalid OTP code. Please check and try again.',
            code: 'VERIFICATION_FAILED'
          };
        } catch (verifyError) {
          console.warn('Twilio Verify check failed, falling back to Edge Function:', verifyError);
        }
      }

      // Try Edge Function verification first (if available)
      try {
        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
        
        if (sessionError) {
          console.warn('Session error (continuing as guest):', sessionError);
        }
        
        const authToken = session?.access_token;

        const functionCall = async () => {
          return await supabase?.functions?.invoke('verify-otp', {
            body: { 
              phoneNumber: formattedPhone, 
              otpCode: otpCode?.trim() 
            },
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
          });
        };

        const { data, error } = await this._retryEdgeFunctionCall(functionCall);

        if (error) {
          console.warn('Edge function verification failed, falling back to client-side verification:', error);
          // Fall back to client-side verification
          return await this._clientSideVerification(formattedPhone, otpCode);
        }

        if (data?.success) {
          return { 
            success: true, 
            data: {
              ...data,
              verificationMethod: 'edge_function'
            }
          };
        } else {
          return { 
            success: false, 
            error: data?.error || 'Invalid OTP code. Please check and try again.',
            code: 'VERIFICATION_FAILED'
          };
        }
      } catch (edgeFunctionError) {
        console.warn('Edge function not available, using client-side verification:', edgeFunctionError);
        return await this._clientSideVerification(formattedPhone, otpCode);
      }
    } catch (error) {
      console.error('Client-side verification error:', error);
      return this._handleEdgeFunctionError(error, 'verify-otp');
    }
  }

  // Fallback client-side verification
  async _clientSideVerification(formattedPhone, otpCode) {
    try {
      // Check if user is authenticated (optional for registration flow)
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      const userId = user?.id || null;

      let otpRecord;

      if (userId) {
        // Authenticated user - get OTP by user_id and phone
        const { data, error: fetchError } = await supabase?.from('otp_verifications')?.select('*')?.eq('user_id', userId)?.eq('phone_number', formattedPhone)?.eq('status', 'pending')?.order('created_at', { ascending: false })?.limit(1)?.single();

        if (fetchError || !data) {
          return { 
            success: false, 
            error: 'No valid OTP found. Please request a new OTP.',
            code: 'OTP_NOT_FOUND'
          };
        }
        otpRecord = data;
      } else {
        // Guest user - get OTP by phone number only
        const { data, error: fetchError } = await supabase?.from('otp_verifications')?.select('*')?.eq('phone_number', formattedPhone)?.eq('status', 'pending')?.is('user_id', null)?.order('created_at', { ascending: false })?.limit(1)?.single();

        if (fetchError || !data) {
          return { 
            success: false, 
            error: 'No valid OTP found. Please request a new OTP.',
            code: 'OTP_NOT_FOUND'
          };
        }
        otpRecord = data;
      }

      // Check if OTP has expired
      const now = new Date();
      const expiryTime = new Date(otpRecord.expires_at);
      
      if (now > expiryTime) {
        // Mark as expired
        await supabase?.from('otp_verifications')?.update({ status: 'expired' })?.eq('id', otpRecord?.id);
        
        return { 
          success: false, 
          error: 'OTP has expired. Please request a new one.',
          code: 'OTP_EXPIRED'
        };
      }

      // Verify OTP code
      if (otpRecord?.otp_code !== otpCode?.trim()) {
        return { 
          success: false, 
          error: 'Invalid OTP code. Please check and try again.',
          code: 'INVALID_OTP'
        };
      }

      // Mark OTP as verified
      const { error: updateError } = await supabase?.from('otp_verifications')?.update({ 
          status: 'verified',
          verified_at: new Date()?.toISOString()
        })?.eq('id', otpRecord?.id);

      if (updateError) {
        console.error('Failed to update OTP status:', updateError);
        return { 
          success: false, 
          error: 'Verification failed. Please try again.',
          code: 'UPDATE_FAILED'
        };
      }

      // For guest users, store verification status in localStorage
      if (!userId) {
        localStorage.setItem('guestPhoneVerified', JSON.stringify({
          phoneNumber: formattedPhone,
          verifiedAt: new Date()?.toISOString(),
          sessionId: otpRecord?.session_id
        }));
      } else {
        // Update user profile to mark phone as verified (for authenticated users)
        const { error: profileError } = await supabase?.from('user_profiles')?.upsert({
            id: userId,
            mobile_number: formattedPhone,
            phone_verified: true,
            phone_verified_at: new Date()?.toISOString()
          });

        if (profileError) {
          console.warn('Failed to update user profile:', profileError);
          // Don't fail the verification if profile update fails
        }
      }

      return { 
        success: true, 
        data: {
          verifiedAt: new Date()?.toISOString(),
          message: 'Phone number verified successfully',
          verificationMethod: 'client_side',
          isGuest: !userId
        }
      };
    } catch (error) {
      console.error('Client-side verification error:', error);
      return this._handleEdgeFunctionError(error, 'client-verification');
    }
  }

  // Enhanced phone verification check that works for both authenticated and guest users
  async isPhoneVerified() {
    try {
      // Check if user is authenticated (optional for registration flow)
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (user) {
        // Authenticated user - check database
        if (!supabase?.from) {
          const error = new Error('Database service not available');
          const errorResult = serviceErrorHandler?.parseServiceError(error, 'supabase-database');
          serviceErrorHandler?.logError(errorResult);
          return errorResult;
        }

        const { data, error } = await supabase?.from('user_profiles')?.select('phone_verified, phone_verified_at, mobile_number')?.eq('id', user?.id)?.single();

        if (error) {
          const errorResult = serviceErrorHandler?.parseServiceError(error, 'phone-verification-check');
          serviceErrorHandler?.logError(errorResult);
          return errorResult;
        }

        return { 
          success: true, 
          data: {
            phoneVerified: data?.phone_verified || false,
            phoneVerifiedAt: data?.phone_verified_at,
            mobileNumber: data?.mobile_number,
            isGuest: false
          }
        };
      } else {
        // Guest user - check localStorage
        const guestVerification = localStorage.getItem('guestPhoneVerified');
        if (guestVerification) {
          try {
            const verificationData = JSON.parse(guestVerification);
            return {
              success: true,
              data: {
                phoneVerified: true,
                phoneVerifiedAt: verificationData?.verifiedAt,
                mobileNumber: verificationData?.phoneNumber,
                isGuest: true
              }
            };
          } catch (error) {
            // Invalid data in localStorage
            localStorage.removeItem('guestPhoneVerified');
          }
        }

        return {
          success: true,
          data: {
            phoneVerified: false,
            phoneVerifiedAt: null,
            mobileNumber: null,
            isGuest: true
          }
        };
      }
    } catch (error) {
      const errorResult = serviceErrorHandler?.parseServiceError(error, 'phone-verification-status');
      serviceErrorHandler?.logError(errorResult);
      return errorResult;
    }
  }

  // Get OTP status for a phone number  
  async getOTPStatus(phoneNumber) {
    try {
      if (!supabase?.from) {
        return { 
          success: false, 
          error: 'Database service not properly configured.' 
        };
      }

      // Format phone number consistently
      let formattedPhone = phoneNumber?.replace(/\D/g, '');
      if (formattedPhone?.length === 10) {
        formattedPhone = '+91' + formattedPhone;
      } else if (!formattedPhone?.startsWith('+')) {
        formattedPhone = '+' + formattedPhone;
      }

      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError || !user) {
        return { success: false, error: 'User not authenticated' };
      }

      const { data, error } = await supabase?.from('otp_verifications')?.select('*')?.eq('user_id', user?.id)?.eq('phone_number', formattedPhone)?.order('created_at', { ascending: false })?.limit(1)?.single();

      if (error) {
        return { success: false, error: error?.message };
      }

      return { success: true, data: data || null };
    } catch (error) {
      return { success: false, error: 'Failed to get OTP status' };
    }
  }

  // Clean up expired OTPs (admin function)
  async cleanupExpiredOTPs() {
    try {
      if (!supabase?.from) {
        return { success: false, error: 'Database service not available' };
      }

      const now = new Date()?.toISOString();

      const { data, error } = await supabase?.from('otp_verifications')?.update({ status: 'expired' })?.lt('expires_at', now)?.eq('status', 'pending')?.select();

      if (error) {
        return { success: false, error: error?.message };
      }

      return { 
        success: true, 
        data: { cleanedCount: data?.length || 0 }
      };
    } catch (error) {
      return { success: false, error: 'Failed to cleanup expired OTPs' };
    }
  }
}

// Create a singleton instance with error handling
let otpService;
try {
  otpService = new OTPService();
} catch (error) {
  console.error('Failed to initialize OTPService:', error);
  otpService = null;
}

export default otpService;
